import type { Locale } from '~/types/locale'

/**
 * Use this middleware to redirect routes along with their locale prefixes.
 * This isn't possible using the built-in `routeRules` config in Nuxt.
 */
export default defineNuxtRouteMiddleware((to) => {
  const routesToRedirect = [
    // Highest performing media SEO pages redirect to new joint page
    {
      from: '/influencer/list/tags/media/',
      to: '/influencer/list/tags/media-journalist/',
      statusCode: 301,
    },
    {
      from: '/influencer/list/tags/journalist/',
      to: '/influencer/list/tags/media-journalist/',
      statusCode: 301,
    },
    { from: '/cgs/', to: '/lp/cgs/', statusCode: 301 },
    { from: '/about/', to: '/lp/about/', statusCode: 301 },
    // plans pages
    { from: '/lp/plans/', to: '/plans/', statusCode: 301 },
    {
      from: '/lp/priority-support/',
      to: '/plans/priority-support/',
      statusCode: 301,
    },
    {
      from: '/lp/constantly-releases/',
      to: '/plans/constantly-releases/',
      statusCode: 301,
    },
    {
      from: '/lp/groover-plus-coaching-learning/',
      to: '/plans/coaching-learning/',
      statusCode: 301,
    },
    {
      from: '/lp/occasionally-releases/',
      to: '/plans/occasionally-releases/',
      statusCode: 301,
    },
    {
      from: '/lp/rarely-releases/',
      to: '/plans/rarely-releases/',
      statusCode: 301,
    },
    {
      from: '/lp/hype/',
      to: '/plans/hype/',
      statusCode: 301,
    },
    {
      from: '/lp/exclusive-opportunities/',
      to: '/plans/exclusive-opportunities/',
      statusCode: 301,
    },
  ]

  const {
    $localePath,
    $i18n: { locales },
  } = useNuxtApp()

  // Get the path without leading slash first
  const pathWithoutLeadingSlash = to.path.startsWith('/')
    ? to.path.substring(1)
    : to.path

  // Use supported locales from the i18n config
  const supportedLocaleCodes = Array.isArray(locales.value)
    ? locales.value.map((locale) =>
        typeof locale === 'string' ? locale : locale.code,
      )
    : []

  // Check if path starts with a supported locale prefix
  const localePrefix = pathWithoutLeadingSlash.split('/')[0]
  const hasLocalePrefix = supportedLocaleCodes.includes(localePrefix as Locale)

  // Extract the path without locale prefix
  const normalizedPathWithoutLocalePrefix = hasLocalePrefix
    ? '/' + pathWithoutLeadingSlash.substring(localePrefix.length + 1)
    : to.path

  // Find the route to redirect based on the normalized path
  const foundRouteToRedirect = routesToRedirect.find(
    (redirectRoute) => normalizedPathWithoutLocalePrefix === redirectRoute.from,
  )

  if (!foundRouteToRedirect) return

  return navigateTo(
    {
      path: $localePath(foundRouteToRedirect.to),
      query: to.query,
      hash: to.hash,
    },
    { redirectCode: foundRouteToRedirect.statusCode },
  )
})
